import colors from '../colors';

export type ButtonVariants = 'primary'
| 'secondary'
| 'primaryAlternate'
| 'secondaryAlternate';

export type ButtonSizes = 'small' | 'medium' | 'large';

export const buttonKeyVariants = {
  sizes: [
    'small',
    'medium',
    'large'
  ],
  types: [
    'button',
    'submit',
    'reset'
  ],
  tags: [
    'button',
    'a'
  ],
  variants: [
    'primary',
    'secondary',
    'primaryAlternate',
    'secondaryAlternate'
  ]
};

const baseVariantShared = {
  borderRadius: '5px',
  outline: 'none'
};

const pressVariantShared = {
  lineHeight: 1,
  fontSize: '1.125rem',
  borderRadius: '5px',
  outline: 'none',
  margin: 'auto',
  transition: 'all 0.15s ease'
};

const chart = {
  primary: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'indigo.60', // orange.45
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'indigo.60', // orange.45
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'indigo.80', // orange.80
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60' // orange.45
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'indigo.80', // orange.80
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60' // orange.45
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'indigo.80', // orange.80
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60' // orange.45
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'indigo.60', // orange.45
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60', // orange.45
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondary: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'orange.45'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  primaryAlternate: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'orange.45', // indigo.60
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'orange.45', // indigo.60
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'orange.80', // indigo.90
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45' // indigo.60
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'orange.80', // indigo.90
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'orange.45' // indigo.60
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolgrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45' // indigo.60
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'orange.45', // indigo.60
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'orange.45', // indigo.60
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondaryAlternate: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  }
};

const co = {
  primary: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'coolGrey.100',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'coolGrey.70',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'coolGrey.70',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondary: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  primaryAlternate: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'blue.60',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'blue.60',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'blue.90',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'blue.90',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'blue.60',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.60',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondaryAlternate: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'blue.60'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  }
};

const edu = {
  primary: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'indigo.70',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'indigo.70',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'indigo.80',
      backgroundColor: 'indigo.20',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:focus': {
      color: 'indigo.80',
      backgroundColor: 'indigo.20',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:active': {
      color: 'indigo.80',
      backgroundColor: 'indigo.20',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'indigo.70',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondary: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  primaryAlternate: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'indigo.70',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'indigo.70',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'indigo.80',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'indigo.80',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'indigo.70',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondaryAlternate: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.70'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  }
};

const press = {
  primary: {
    ...pressVariantShared,
    color: 'white',
    backgroundColor: 'teal.30',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'teal.30',
    boxShadow: '2px 2px 0 0 rgba(0, 0, 0, 0.25)',
    '&:hover, &:active, &:focus': {
      color: 'teal.50',
      backgroundColor: 'teal.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'teal.50',
      boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)'
    },
    '&:focus': {
      borderWidth: '2px'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'teal.30',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'teal.30',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondary: {
    ...pressVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'teal.50',
    boxShadow: '2px 2px 0 0 rgba(0, 0, 0, 0.25)',
    '&:hover, &:focus, &:active': {
      color: 'teal.50',
      backgroundColor: 'teal.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'teal.50',
      boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)'
    },
    '&:focus': {
      borderWidth: '2px',
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  primaryAlternate: {
    ...pressVariantShared,
    color: 'white',
    backgroundColor: 'indigo.60',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'indigo.60',
    boxShadow: '2px 2px 0 0 rgba(0, 0, 0, 0.25)',
    '&:hover': {
      color: 'white',
      backgroundColor: 'indigo.90',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60',
      boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'indigo.90',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'indigo.60',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondaryAlternate: {
    ...pressVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    boxShadow: '2px 2px 0 0 rgba(0, 0, 0, 0.25)',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.25)',
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  }
};

const spark = {
  primary: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'red.50',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'red.50',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'red.80',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'red.50'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'red.80',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'red.50'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'red.50'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'red.50',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'red.50',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondary: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'red.50'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'red.50'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  primaryAlternate: {
    ...baseVariantShared,
    color: 'white',
    backgroundColor: 'indigo.90',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'indigo.90',
    boxShadow: `4px 4px 0 0 ${colors.coolGrey[10]}`,
    '&:hover': {
      color: 'white',
      backgroundColor: 'indigo.60',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.90'
    },
    '&:focus': {
      color: 'white',
      backgroundColor: 'indigo.60',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.90'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'white',
      backgroundColor: 'indigo.90',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.90',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  },
  secondaryAlternate: {
    ...baseVariantShared,
    color: 'coolGrey.100',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'coolGrey.100',
    '&:hover': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100'
    },
    '&:focus': {
      color: 'coolGrey.100',
      backgroundColor: 'coolGrey.5',
      borderWidth: '2px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:active': {
      color: 'white',
      backgroundColor: 'coolGrey.100',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'indigo.60'
    },
    '&:disabled': {
      color: 'coolGrey.100',
      backgroundColor: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'coolGrey.100',
      boxShadow: 'none',
      cursor: 'default',
      opacity: '0.35'
    }
  }
};

const buttons = {
  products: {
    chart,
    co,
    edu,
    press,
    spark
  }
};

export default buttons;
